import {
	Alert,
	AlertTitle,
	Box,
	InputBase,
	Paper,
	Stack,
	Typography,
} from "@mui/material";
import dayjs from "dayjs";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
	listNotifications,
	RESET_NOTIFICATIONS_QUERY,
	UPDATE_NOTIFICATIONS_QUERY,
} from "../../actions/form";
import { ESRState } from "../../types/data";

import { SearchOutlined } from "@mui/icons-material";
import { useCurrentUser } from "../../hooks/useCurrentUser";
import { DataAction } from "../../types/action";
import { FacetFilter } from "../FacetFilter";
import { StatusFacetFilter } from "../StatusFacetFilter";
import { ESRNotifications } from "./ESRNotifications";
import { listAllUnresolvedAndUnreadThreads } from "../../actions/util";
import { useDocumentTitle } from "../../hooks/useDocumentTitle";

export const NotificationsList = ({ testOnly = false }: { testOnly?: boolean } = {}): JSX.Element => {
	useDocumentTitle("IDOT – TREC Take Action");
	const dispatch = useDispatch();
	const [user, setUser] = useCurrentUser();
	const notifications = useSelector((state: ESRState) => state.notifications);
	const [alertOpen, setAlertOpen] = useState(true);
	const [sidebarOpen, setSidebarOpen] = useState(false);
	const query = useSelector((s: ESRState) => s.notificationlist_query);
	const facets = useSelector((s: ESRState) => s.notifications_facets);
	const [searchString, setSearchString] = useState("");
	const timerRef = useRef(-1);
	const statusFilter = query.status as string[] | undefined;
	const visibleNotifications = notifications
		.map((group) => ({
			...group,
			notifications: statusFilter?.length
				? group.notifications.filter((notif) =>
						statusFilter.includes(String(notif.status)),
					)
				: group.notifications,
		}))
		.filter((group) => group.notifications.length > 0);

	useEffect(() => {
		window.clearTimeout(timerRef.current);
		timerRef.current = window.setTimeout(() => {
			dispatch<DataAction>({
				type: UPDATE_NOTIFICATIONS_QUERY,
				updates: {
					search: searchString,
				},
			});
		}, 100);
	}, [searchString]);

	// useEffect to clear notifications filter
	useEffect(() => {
		dispatch<DataAction>({
			type: RESET_NOTIFICATIONS_QUERY,
		});
		dispatch({
			type: UPDATE_NOTIFICATIONS_QUERY,
			updates: {
				status: ["0", "1"], // Unread & Read
				page: 0,
			},
		});
	}, []);

	useEffect(() => {
		dispatch(listNotifications(testOnly));
	}, [user, query]);
	useEffect(() => {
		if(user){
			dispatch(listAllUnresolvedAndUnreadThreads());
		}
	}, [user]);

	/**
	 * Load current notifications the user can see.
	 */
	return (
		<>
			<Box sx={{ display: "flex", height: "100%" }}>
				<Box
					sx={{
						flex: "auto",
						p: "60px 60px",
						height: "100%",
						boxSizing: "border-box",
						overflow: "scroll",
					}}
				>
					<Box sx={{ display: "flex" }}>
						<Typography component="h1" sx={{ fontSize: 24, fontWeight: 700 }}>
							Take Actions
						</Typography>
						<Box sx={{ flex: 1 }}></Box>
						<Paper
							square
							elevation={0}
							sx={{
								p: "2px 4px",
								display: "flex",
								alignItems: "center",
								width: 400,
								bgcolor: "#0000000A",
							}}
						>
							<InputBase
								sx={{
									ml: 2,
									flex: 1,
									"& input::placeholder": {
										color: "#4a4a4a",
										opacity: 1,
									},
								}}
								value={searchString}
								onChange={(e) => setSearchString(e.target.value)}
								placeholder="Search by ESR #, Sponsor Type, County, or Route"
							/>
							<SearchOutlined />
						</Paper>
					</Box>
					<Stack
						direction="row"
						gap="12px"
						flexWrap="wrap"
						alignItems="center"
						sx={{ mt: "40px", mb: "30px" }}
					>
						{facets.map(({ key, label, type, values }, i) => {
							const Filter = type === "status" ? StatusFacetFilter : FacetFilter;
							return [
								i > 0 && (
									<Typography
										key={i}
										color="gray"
										fontWeight="semibold"
										fontSize={12}
									>
										AND
									</Typography>
								),
								<Filter
									key={key}
									label={label}
									value={query[key] ?? []}
									options={values}
									onChange={(value) => {
										dispatch({
											type: UPDATE_NOTIFICATIONS_QUERY,
											updates: {
												[key]: value,
												page: 0,
											},
										});
									}}
								/>,
							];
						})}
					</Stack>

					{alertOpen ? (
						<Alert
							icon={false}
							onClose={() => {
								setAlertOpen(false);
							}}
							sx={{ my: "30px", px: "40px" }}
							severity="warning"
						>
							<AlertTitle>
								Hi{user ? " " + user.first_name : ""}, today is{" "}
								{dayjs().format("MM/DD/YYYY")},{" "}
								{new Date().toLocaleDateString("en-US", {
									weekday: "long",
								})}
								.
							</AlertTitle>
							TREC recommends you take the following actions promptly.
						</Alert>
					) : null}
					{visibleNotifications.map(({ esrId, notifications }) => (
						<ESRNotifications
							key={esrId}
							esrId={esrId}
							notifications={notifications}
						/>
					))}
					<Box
						sx={{
							boxShadow: "-1px 0px 0px 0px rgba(0, 0, 0, 0.12)",
							display: sidebarOpen ? "block" : "none",
							width: 610,
							background: "#003E6205",
							p: "40px 22px",
						}}
					></Box>
				</Box>
			</Box>
		</>
	);
};
